import {
  isSameDay,
  startOfDay,
  isWeekend,
  getYear,
  eachDayOfInterval,
  formatISO,
  differenceInBusinessDays,
  differenceInDays,
  isValid,
  isWithinInterval,
  eachWeekendOfYear,
} from 'date-fns';

import { ICalendarService } from './calendar.interface';

export class Calendar implements ICalendarService {
  private holidaysAndWeekends: Record<number, Record<string, boolean>> = {};

  private holidays: Record<number, Record<string, boolean>> = {};

  /**
   * Загрузить календарь выходных и праздничных дней
   *
   * @param date - Дата, по которой будет вычисляться год для загружаемого календаря.
   * @param id - Id сотрудника необходим, если загружается календарь для конкретного сотрудника.
   * @returns Promise<void>
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async download(date = new Date(), id?: string) {
    const year = getYear(date);
    const defaultYear = 2021;

    if (year < defaultYear) return;

    this.holidaysAndWeekends[year] = eachWeekendOfYear(date).reduce(
      (acc, day) => ({
        ...acc,
        [formatISO(day, { representation: 'date' })]: true,
      }),
      {},
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async downloadWorkSchedule(year: number, employeeId: string) {
    return null;
  }

  /**
   * Преобразовать дату в формат ISO
   *
   * @param date - Дата, которая должна быть преобразована.
   * @param options - Параметры для преобразования к ISO.
   * @returns string
   */
  formatISO(
    date: Date,
    options: { representation: 'complete' | 'date' | 'time' } = {
      representation: 'complete',
    },
  ) {
    return formatISO(date, options);
  }

  /**
   * Определить является выбранная дата выходным или праздничным днем
   * на основе ранее загруженных календарей
   *
   * @param date - Выбранная дата.
   * @returns boolean
   */
  isHolidayOrWeekend(date: Date): boolean {
    const year = getYear(date);

    if (this.holidaysAndWeekends[year]) {
      return !!this.holidaysAndWeekends[year][
        formatISO(date, { representation: 'date' })
      ];
    }

    return isWeekend(date);
  }

  /**
   * Определить является выбранная дата праздничным днем
   * на основе ранее загруженных календарей
   *
   * @param date - Выбранная дата.
   * @returns boolean
   */
  isHoliday(date: Date): boolean {
    const year = getYear(date);

    if (this.holidays[year]) {
      return !!this.holidays[year][formatISO(date, { representation: 'date' })];
    }

    return false;
  }

  /**
   * Определить совпадает год, месяц и день у двух выбранных дат
   *
   * @param date1 - Дата №1.
   * @param date2 - Дата №2.
   * @returns boolean
   */
  isSameDay(date1: Date, date2: Date): boolean {
    return isSameDay(date1, date2);
  }

  /**
   * Определить количество полных дней между двумя датами
   * Дробные дни округляются до нуля
   *
   * @param date1 - Дата №1.
   * @param date2 - Дата №2.
   * @param options - Опции для исключения праздничных или рабочих дней.
   * @returns number
   */
  differenceInDays(
    date1: Date,
    date2: Date,
    options?: { excludeHolidays?: boolean; excludeWorkingDays?: boolean },
  ): number {
    if (options?.excludeHolidays) {
      return (
        differenceInDays(date1, date2) -
        this.countOfHolidaysInInterval(date2, date1)
      );
    }

    if (options?.excludeWorkingDays) {
      return differenceInBusinessDays(date1, date2);
    }

    return differenceInDays(date1, date2);
  }

  /**
   * Получить дату начала выбранного дня
   * Результат будет в локальной временной зоне
   *
   * @param date - Выбранная дата.
   * @returns Date
   */
  startOfDay(date: Date): Date {
    return startOfDay(date);
  }

  /**
   * Входит ли дата в один из интервалов
   *
   * @param date - Выбранная дата.
   * @param intervals - Массив интервалов.
   * @returns boolean
   */
  isWithinIntervals(date: Date, intervals: [start: Date, end: Date][]) {
    return intervals.some((interval) =>
      isWithinInterval(date, { start: interval[0], end: interval[1] }),
    );
  }

  /**
   * Определить количество праздничных дней между двумя датами
   * на основе ранее загруженных календарей
   *
   * @param date1 - Дата №1.
   * @param date2 - Дата №2.
   * @returns number
   */
  countOfHolidaysInInterval(date1: Date, date2: Date) {
    return eachDayOfInterval({
      start: date1 < date2 ? date1 : date2,
      end: date1 < date2 ? date2 : date1,
    }).filter((day) => this.isHoliday(day)).length;
  }

  /**
   * Определить является выбранная дата валидной
   *
   * @param date - Выбранная дата.
   * @returns boolean
   */
  isValidDate(date: string | null | Date) {
    return isValid(date);
  }
}
